import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { normalizeRut } from '@/utils/rut';
import { appMobileProfileEmployeesService } from '@/api/domains/employee/appMobileProfileEmployees/appMobileProfileEmployees.service';
import { mapAppMobileProfileEmployee } from '@/api/domains/employee/appMobileProfileEmployees/appMobileProfileEmployees.mapper';
import type { AppMobileProfileEmployee } from '@/api/domains/employee/appMobileProfileEmployees/appMobileProfileEmployees.types';


const PAGE_SIZE = 100;

// ── Return type interface ──────────────────────────────────────────────────

export interface UseMobileAppEmployeeAssignmentResult {
  // Empleados sin el perfil seleccionado (panel izquierdo)
  unassigned: AppMobileProfileEmployee[];
  filteredUnassigned: AppMobileProfileEmployee[];
  unassignedSearchText: string;
  onUnassignedSearch: (text: string) => void;

  // Empleados con el perfil seleccionado (panel derecho)
  assigned: AppMobileProfileEmployee[];
  filteredAssigned: AppMobileProfileEmployee[];
  assignedSearchText: string;
  onAssignedSearch: (text: string) => void;

  // Selección — panel sin asignar
  selectedUnassignedIds: Set<number>;
  onToggleUnassigned: (employeeId: number) => void;
  onSelectAllUnassigned: () => void;
  isAllUnassignedSelected: boolean;
  isUnassignedIndeterminate: boolean;

  // Selección — panel asignados
  selectedAssignedIds: Set<number>;
  onToggleAssigned: (employeeId: number) => void;
  onSelectAllAssigned: () => void;
  isAllAssignedSelected: boolean;
  isAssignedIndeterminate: boolean;

  // Movimiento entre paneles
  onAdd: () => void;
  onRemove: () => void;
  isAddDisabled: boolean;
  isRemoveDisabled: boolean;

  // Persistencia de cambios
  onSave: () => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
  hasPendingChanges: boolean;

  isLoading: boolean;
}


// ── Helpers ──────────────────────────────────────────────────────────────────

function filterByNameOrRut(
  employees: AppMobileProfileEmployee[],
  searchText: string,
): AppMobileProfileEmployee[] {
  const query = searchText.toLowerCase().trim();
  if (!query) return employees;
  // El RUT se almacena formateado (11.111.111-1); normalizamos ambos lados para que
  // la búsqueda matchee tanto con puntos/guión como sin ellos (11111111).
  const rutQuery = normalizeRut(searchText);
  return employees.filter(
    (employee) =>
      employee.fullName.toLowerCase().includes(query) ||
      normalizeRut(employee.rut).includes(rutQuery),
  );
}


// ── Hook ───────────────────────────────────────────────────────────────────

export function useMobileAppEmployeeAssignment(
  profileId: number | null,
): UseMobileAppEmployeeAssignmentResult {
  const { t: translate } = useTranslation();
  const { showApiError, showSuccess } = useUI();
  const { createSignal } = useAbortController();

  const [employees, setEmployees] = useState<AppMobileProfileEmployee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [unassignedSearchText, setUnassignedSearchText] = useState('');
  const [assignedSearchText, setAssignedSearchText] = useState('');
  const hasLoadedRef = useRef(false);

  // Selección por panel.
  const [selectedUnassignedIds, setSelectedUnassignedIds] = useState<Set<number>>(new Set());
  const [selectedAssignedIds, setSelectedAssignedIds] = useState<Set<number>>(new Set());

  // Movimientos locales (aún no persistidos). Un empleado movido a asignados vive en
  // locallyAssignedIds; uno movido a sin asignar, en locallyUnassignedIds. Estos overrides
  // se aplican sobre la partición derivada del backend.
  const [locallyAssignedIds, setLocallyAssignedIds] = useState<Set<number>>(new Set());
  const [locallyUnassignedIds, setLocallyUnassignedIds] = useState<Set<number>>(new Set());

  const loadEmployeesError = translate('authorization:mobileAppPermissions.loadEmployeesError');
  const saveSuccessMessage = translate('authorization:mobileAppPermissions.messages.saveSuccess');
  const saveErrorMessage = translate('authorization:mobileAppPermissions.messages.saveError');

  // Carga (paginando) la lista completa de empleados elegibles y la deja en estado.
  // Se reutiliza en la carga inicial y en la recarga tras guardar exitosamente.
  const loadAllEmployees = useCallback(async () => {
    const signal = createSignal();
    setIsLoading(true);
    const accumulated: AppMobileProfileEmployee[] = [];
    let page = 1;

    while (true) {
      const response = await appMobileProfileEmployeesService.list(
        { page, pageSize: PAGE_SIZE },
        signal,
      );

      if (response.error?.code === 'ABORTED') return;

      if (!response.success || !response.data) {
        showApiError(response, loadEmployeesError);
        setEmployees([]);
        setIsLoading(false);
        return;
      }

      const items = (response.data.data ?? []).map(mapAppMobileProfileEmployee);
      accumulated.push(...items);

      const totalRecords = response.data.pagination?.totalRecords ?? accumulated.length;
      if (items.length === 0 || accumulated.length >= totalRecords) break;
      page += 1;
    }

    hasLoadedRef.current = true;
    setEmployees(accumulated);
    setIsLoading(false);
  }, [createSignal, showApiError, loadEmployeesError]);

  // La lista de empleados elegibles no depende del perfil: la partición asignados/sin
  // asignar se hace client-side. Por eso se carga una sola vez (la primera vez que hay un
  // perfil seleccionado); cambiar de perfil solo re-particiona, sin volver a consultar.
  useEffect(() => {
    if (profileId === null || hasLoadedRef.current) return;
    void loadAllEmployees();
  }, [profileId, loadAllEmployees]);

  // Reinicia buscadores, selección y movimientos locales al cambiar de perfil.
  useEffect(() => {
    setUnassignedSearchText('');
    setAssignedSearchText('');
    setSelectedUnassignedIds(new Set());
    setSelectedAssignedIds(new Set());
    setLocallyAssignedIds(new Set());
    setLocallyUnassignedIds(new Set());
  }, [profileId]);

  // El backend devuelve una fila por (empleado, perfil): un empleado asignado a varios
  // perfiles aparece repetido. Agrupamos por empleado conservando el conjunto de perfiles
  // que tiene, para que cada empleado figure una sola vez en la partición.
  const employeesWithProfiles = useMemo(() => {
    const byEmployeeId = new Map<
      number,
      { employee: AppMobileProfileEmployee; profileIds: Set<number> }
    >();
    for (const row of employees) {
      let entry = byEmployeeId.get(row.employeeId);
      if (!entry) {
        entry = { employee: row, profileIds: new Set<number>() };
        byEmployeeId.set(row.employeeId, entry);
      }
      if (row.appProfileId !== null) entry.profileIds.add(row.appProfileId);
    }
    return Array.from(byEmployeeId.values());
  }, [employees]);

  // Partición client-side por perfil seleccionado, un empleado en un solo panel.
  // Se aplican los movimientos locales: un empleado del backend puede haber sido movido
  // al otro panel (locallyAssignedIds / locallyUnassignedIds) sin persistir todavía.
  const isAssignedEmployee = useCallback(
    (entry: { employee: AppMobileProfileEmployee; profileIds: Set<number> }): boolean => {
      const employeeId = entry.employee.employeeId;
      if (locallyUnassignedIds.has(employeeId)) return false;
      if (locallyAssignedIds.has(employeeId)) return true;
      return profileId !== null && entry.profileIds.has(profileId);
    },
    [profileId, locallyAssignedIds, locallyUnassignedIds],
  );

  const assigned = useMemo(
    () =>
      profileId === null
        ? []
        : employeesWithProfiles
          .filter((entry) => isAssignedEmployee(entry))
          .map((entry) => entry.employee),
    [employeesWithProfiles, profileId, isAssignedEmployee],
  );
  const unassigned = useMemo(
    () =>
      profileId === null
        ? []
        : employeesWithProfiles
          .filter((entry) => !isAssignedEmployee(entry))
          .map((entry) => entry.employee),
    [employeesWithProfiles, profileId, isAssignedEmployee],
  );

  // Filtrado en tiempo real por nombre o RUT.
  const filteredUnassigned = useMemo(
    () => filterByNameOrRut(unassigned, unassignedSearchText),
    [unassigned, unassignedSearchText],
  );
  const filteredAssigned = useMemo(
    () => filterByNameOrRut(assigned, assignedSearchText),
    [assigned, assignedSearchText],
  );

  const onUnassignedSearch = useCallback((text: string) => setUnassignedSearchText(text), []);
  const onAssignedSearch = useCallback((text: string) => setAssignedSearchText(text), []);

  // ── Selección — panel sin asignar ─────────────────────────────────────
  const onToggleUnassigned = useCallback((employeeId: number) => {
    setSelectedUnassignedIds((previous) => {
      const next = new Set(previous);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  }, []);

  const isAllUnassignedSelected = useMemo(
    () =>
      filteredUnassigned.length > 0 &&
      filteredUnassigned.every((employee) => selectedUnassignedIds.has(employee.employeeId)),
    [filteredUnassigned, selectedUnassignedIds],
  );

  const isUnassignedIndeterminate = useMemo(
    () =>
      !isAllUnassignedSelected &&
      filteredUnassigned.some((employee) => selectedUnassignedIds.has(employee.employeeId)),
    [filteredUnassigned, selectedUnassignedIds, isAllUnassignedSelected],
  );

  const onSelectAllUnassigned = useCallback(() => {
    setSelectedUnassignedIds((previous) => {
      const next = new Set(previous);
      if (isAllUnassignedSelected) {
        filteredUnassigned.forEach((employee) => next.delete(employee.employeeId));
      } else {
        filteredUnassigned.forEach((employee) => next.add(employee.employeeId));
      }
      return next;
    });
  }, [filteredUnassigned, isAllUnassignedSelected]);

  // ── Selección — panel asignados ───────────────────────────────────────
  const onToggleAssigned = useCallback((employeeId: number) => {
    setSelectedAssignedIds((previous) => {
      const next = new Set(previous);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  }, []);

  const isAllAssignedSelected = useMemo(
    () =>
      filteredAssigned.length > 0 &&
      filteredAssigned.every((employee) => selectedAssignedIds.has(employee.employeeId)),
    [filteredAssigned, selectedAssignedIds],
  );

  const isAssignedIndeterminate = useMemo(
    () =>
      !isAllAssignedSelected &&
      filteredAssigned.some((employee) => selectedAssignedIds.has(employee.employeeId)),
    [filteredAssigned, selectedAssignedIds, isAllAssignedSelected],
  );

  const onSelectAllAssigned = useCallback(() => {
    setSelectedAssignedIds((previous) => {
      const next = new Set(previous);
      if (isAllAssignedSelected) {
        filteredAssigned.forEach((employee) => next.delete(employee.employeeId));
      } else {
        filteredAssigned.forEach((employee) => next.add(employee.employeeId));
      }
      return next;
    });
  }, [filteredAssigned, isAllAssignedSelected]);

  // ── Movimiento entre paneles ──────────────────────────────────────────
  const onAdd = useCallback(() => {
    if (selectedUnassignedIds.size === 0) return;
    setLocallyAssignedIds((previous) => {
      const next = new Set(previous);
      selectedUnassignedIds.forEach((id) => next.add(id));
      return next;
    });
    setLocallyUnassignedIds((previous) => {
      const next = new Set(previous);
      selectedUnassignedIds.forEach((id) => next.delete(id));
      return next;
    });
    setSelectedUnassignedIds(new Set());
  }, [selectedUnassignedIds]);

  const onRemove = useCallback(() => {
    if (selectedAssignedIds.size === 0) return;
    setLocallyUnassignedIds((previous) => {
      const next = new Set(previous);
      selectedAssignedIds.forEach((id) => next.add(id));
      return next;
    });
    setLocallyAssignedIds((previous) => {
      const next = new Set(previous);
      selectedAssignedIds.forEach((id) => next.delete(id));
      return next;
    });
    setSelectedAssignedIds(new Set());
  }, [selectedAssignedIds]);

  const isAddDisabled = selectedUnassignedIds.size === 0;
  const isRemoveDisabled = selectedAssignedIds.size === 0;

  // ── Persistencia ──────────────────────────────────────────────────────
  const hasPendingChanges = locallyAssignedIds.size > 0 || locallyUnassignedIds.size > 0;

  // Guardado secuencial: primero asigna, luego quita. Si cualquier llamada falla, se
  // muestra el error y se conservan TODOS los cambios locales. Solo si ambas operaciones
  // son exitosas se limpian los movimientos pendientes y se recarga desde el backend.
  const onSave = useCallback(async () => {
    if (profileId === null) return;
    const employeeIdsToAssign = Array.from(locallyAssignedIds);
    const employeeIdsToRemove = Array.from(locallyUnassignedIds);
    if (employeeIdsToAssign.length === 0 && employeeIdsToRemove.length === 0) return;

    setIsSaving(true);
    try {
      if (employeeIdsToAssign.length > 0) {
        const response = await appMobileProfileEmployeesService.assign({
          profileId,
          employeeIds: employeeIdsToAssign.join(','),
        });
        if (!response.success) {
          showApiError(response, saveErrorMessage);
          return;
        }
      }

      if (employeeIdsToRemove.length > 0) {
        const response = await appMobileProfileEmployeesService.remove({
          profileId,
          employeeIds: employeeIdsToRemove,
        });
        if (!response.success) {
          showApiError(response, saveErrorMessage);
          return;
        }
      }

      showSuccess(saveSuccessMessage);
      setSelectedUnassignedIds(new Set());
      setSelectedAssignedIds(new Set());
      // Se recarga PRIMERO y recién después se limpian los movimientos locales. Si se
      // limpiaran antes, la partición caería al dato viejo de `employees` (aún sin la
      // recarga) y los empleados recién movidos saltarían de vuelta a su columna original
      // durante los segundos que tarda la repaginación. Manteniendo los overrides hasta
      // que llega el dato fresco, los paneles muestran el estado correcto sin parpadeo.
      await loadAllEmployees();
      setLocallyAssignedIds(new Set());
      setLocallyUnassignedIds(new Set());
    } finally {
      setIsSaving(false);
    }
  }, [
    profileId,
    locallyAssignedIds,
    locallyUnassignedIds,
    showApiError,
    showSuccess,
    saveErrorMessage,
    saveSuccessMessage,
    loadAllEmployees,
  ]);

  // Descarta los movimientos locales pendientes y la selección, devolviendo ambos paneles
  // al estado persistido en el backend. No re-consulta: la partición se re-deriva sola.
  const onCancel = useCallback(() => {
    setLocallyAssignedIds(new Set());
    setLocallyUnassignedIds(new Set());
    setSelectedUnassignedIds(new Set());
    setSelectedAssignedIds(new Set());
  }, []);

  return {
    unassigned,
    filteredUnassigned,
    unassignedSearchText,
    onUnassignedSearch,
    assigned,
    filteredAssigned,
    assignedSearchText,
    onAssignedSearch,
    selectedUnassignedIds,
    onToggleUnassigned,
    onSelectAllUnassigned,
    isAllUnassignedSelected,
    isUnassignedIndeterminate,
    selectedAssignedIds,
    onToggleAssigned,
    onSelectAllAssigned,
    isAllAssignedSelected,
    isAssignedIndeterminate,
    onAdd,
    onRemove,
    isAddDisabled,
    isRemoveDisabled,
    onSave,
    onCancel,
    isSaving,
    hasPendingChanges,
    isLoading,
  };
}

export default useMobileAppEmployeeAssignment;
